import { BaseCategory } from "./BaseCategory";
import { AuthoringBlockType, HeaderPositionType, ListStyleType, TextStyleType } from "../constants";
import { SlideCategories } from "./CategoryController";
import { SlideModelBuilder } from "./SlideModelBuilder";
import { v4 as uuid } from "uuid";

export class ProductScreenshotCategory extends BaseCategory {
    get name() {
        return "Product Screenshot";
    }

    get type() {
        return SlideCategories.PRODUCT_SCREENSHOT;
    }

    get icon() {
        return null;
    }

    get description() {
        return "Use for showcasing product features, design, and user experience with a device mockup (phone, laptop, computer, web, tablet, etc) and supporting bullet points.";
    }

    get slideTypes() {
        return [];
    }

    getPrompt() {
        return `Create a product screenshot slide based on the user's prompt.
        Provide a description of an image to display within the device mockup frame. Do not include the device frame in the image.
        Specify the device type: laptop, phone, desktop, watch, or tablet.`;
    }

    getPromptSchema() {
        return {
            text: "richtext for the slide",
            // image: "a description of an image to display within the device mockup frame. Do not include the device frame in the image.",
            deviceType: "device type: laptop, phone, desktop, watch, or tablet (defaults to 'laptop')",
            // conclusion: "an optional short conclusion or takeaway for the slide",
        };
    }

    // Map generic device types to specific Apple frame IDs
    mapDeviceTypeToFrameType(deviceType) {
        const deviceMap = {
            "laptop": "macBook_pro_14_inch",
            "phone": "iphone_15_pro",
            "desktop": "iMac_24_inch",
            "watch": "apple_watch_ultra",
            "tablet": "ipad_pro_13_inch_portait"
        };

        return deviceMap[deviceType?.toLowerCase()] || deviceMap["laptop"];
    }

    getImagePrompt() {
        return "If the user mentions a product or screen to show, provide a search term for finding an appropriate product screenshot or UI image.";
    }

    getSlideModels(model, options = {}) {
        let models = [
            this.buildProductScreenshotSlide(model),
        ];

        return models;
    }

    buildProductScreenshotSlide(model) {
        let builder = new SlideModelBuilder("product_laptop", model);
        builder.createHeader({ heading: model.slideTitle, position: HeaderPositionType.TOP });

        const frameType = this.mapDeviceTypeToFrameType(model.deviceType);
        const features = model.features || [];

        // Create the media item (device mockup)
        const mediaItem = {
            isMedia: true,
            size: model.deviceType == "phone" ? 300 : 600,
            text: null,
            frameType: frameType,
        };

        // Add image if provided
        if (model.slideImage?.prompt || model.productImageSearch) {
            const imagePrompt = model.slideImage?.prompt || model.productImageSearch;
            mediaItem.__generateImage = {
                search: imagePrompt,
                searchStock: imagePrompt,
                assetType: "image",
                isSlideImage: !!model.slideImage?.prompt
            };
        }

        // Create the text item with feature bullets
        const textBlocks = [];
        for (let feature of features) {
            // Add main feature
            textBlocks.push({
                id: uuid(),
                type: AuthoringBlockType.TEXT,
                listStyle: ListStyleType.BULLET,
                textStyle: TextStyleType.BULLET_LIST,
                indent: 0,
                html: feature.mainFeature || feature,
            });

            // Add sub-feature if provided
            if (feature.subFeature) {
                textBlocks.push({
                    id: uuid(),
                    type: AuthoringBlockType.TEXT,
                    listStyle: ListStyleType.TEXT,
                    textStyle: TextStyleType.BULLET_LIST,
                    indent: 1,
                    html: feature.subFeature,
                });
            }
        }

        const textItem = {
            isMedia: false,
            text: {
                blocks: textBlocks
            },
        };

        const items = [mediaItem, textItem];

        builder.createPrimary({
            listStyle: ListStyleType.BULLET,
            items,
            showColumnHeaders: false,
            startNum: 1
        });

        if (model.conclusion) {
            builder.createBottomTray(model.conclusion);
        }

        return builder.model;
    }
}
